import Image from "next/image";
import React from "react";
import { FiInfo } from "react-icons/fi";
import { LuArrowUpRight } from "react-icons/lu";
import { GBButtonWithIcon } from "../CTAButtons";
import ExternalLinkButton from "../ExternalLinkButton";

export interface PricingPlanData {
    name: string;
    idealFor: string;
    cardColor: string;
    textColor: string;
    priceTextColor?: string;
    benefitsColor: string;
    borderColor?: string;
    ctaBackgroundColor: string;
    price?: string;
    pricePeriod?: string;
    popularPlan?: string;
    subText?: {
        text: string;
        tooltip?: string;
    };
    waConChargesText?: string;
    waConChargesLink?: string;
    offerText?: string;
    btnText: string;
    btnLink: string;
    userLimit?: string;
    featureTitle: string;
    featureDetails: {
        icon: string;
        text: string;
        toolTip?: string;
    }[];
}
const containerStyle = {
    height: "100%",
    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)",
};

export interface PlansProps {
    pricingPlanData: PricingPlanData;
    isDollar?: boolean;
}

const Plan: React.FC<PlansProps> = (props) => {
    const { pricingPlanData, isDollar } = props;
    const {
        name,
        idealFor,
        price,
        pricePeriod,
        popularPlan,
        borderColor,
        priceTextColor,
        offerText,
        waConChargesText,
        waConChargesLink,
        btnText,
        btnLink,
        userLimit,
        featureTitle,
        featureDetails,
        cardColor,
        textColor,
        benefitsColor,
        ctaBackgroundColor,
    } = pricingPlanData;
    return (
        <div className={`flex flex-col relative md:w-1/2 md:flex-col lg:w-96 mx-auto ${isDollar ? "mobile-view" : "desktop-view"}`}>
            {popularPlan && (
                <p
                    className="text-base font-normal text-center w-full absolute -top-10 text-white border-t border-l border-r border-solid border-blue-700 rounded-tl rounded-tr mt-5 md:mt-0"
                    style={{ backgroundColor: "#339CFF", width: "100%", paddingTop: "10px", paddingBottom: "15px" }}
                >
                    {popularPlan}
                </p>
            )}
            <div
                className={`py-6 px-4 mb-10 relative ${name === "Scale" ? "border-l border-r border-b border-solid rounded-b mt-5 md:mt-0" : " rounded border border-solid "}`}
                style={{
                    ...containerStyle,
                    background: cardColor && cardColor,
                    borderColor: borderColor,
                }}
            >
                <div className="flex flex-col">
                    <div className="flex items-center gap-3">
                        <h3 className="text-4xl font-semibold pb-3" style={{ color: textColor, fontFamily: "Degular Display" }}>
                            {name}
                        </h3>
                        {name === "Starter" && (
                            <p className="bg-yellow-300 font-semibold rounded-full p-1 text-sm px-2 mb-2" style={{ color: priceTextColor }}>
                                Self-serve
                            </p>
                        )}
                    </div>
                    <p className="p-3 text-sm rounded-md" style={{ minHeight: "80px", backgroundColor: benefitsColor }}>
                        {idealFor}
                    </p>

                    <div className="mt-8" style={!(price && pricePeriod) ? { padding: "52px 0px" } : { padding: "20px 0px" }}>
                        {price && pricePeriod && (
                            <React.Fragment>
                                <p className="text-5xl font-medium" style={{ color: priceTextColor }}>
                                    {price}
                                </p>
                                <span className="text-sm" style={{ color: priceTextColor }}>
                                    {pricePeriod}
                                </span>
                            </React.Fragment>
                        )}
                    </div>

                    <div className="flex flex-col" style={{ minHeight: "100px" }}>
                        <GBButtonWithIcon
                            rightIcon={LuArrowUpRight}
                            href={btnLink}
                            rel="noreferrer"
                            target="_blank"
                            style={{
                                color: "white",
                                background: ctaBackgroundColor,
                                padding: "16px 16px",
                                borderRadius: "4px",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "10px",
                            }}
                        >
                            {btnText}
                        </GBButtonWithIcon>
                        <div className="flex items-center gap-2 pt-2">
                            <ExternalLinkButton
                                href={waConChargesLink}
                                rel="noreferrer"
                                style={{ color: textColor, padding: "16px 0px 20px 0px", fontSize: "14px", alignItems: "center", textDecoration: "underline" }}
                            >
                                {waConChargesText}
                            </ExternalLinkButton>
                            {offerText && (
                                <span className="rounded-full px-2 py-1 font-bold text-purple-900 text-sm" style={{ backgroundColor: "#E9D8FD" }}>
                                    {offerText}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="p-2" style={{ borderTop: "1px solid #000" }} />
                <React.Fragment>
                    <p className={` ${!userLimit ? "pb-8" : "font-medium pb-8"}`}>{userLimit}</p>

                    <p className="font-semibold pb-5">{featureTitle}</p>

                    <div className="flex flex-col pb-6 gap-3" style={{ height: "300px" }}>
                        {featureDetails.map((data, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                                <Image height={20} width={20} src={data.icon} alt="Blue Tick" className="w-5 h-5" />
                                <p>{data.text}</p>
                                {data.toolTip && (
                                    <div className="relative group ml-auto mt-1 inline-block">
                                        <FiInfo className="text-base cursor-pointer" />
                                        <span className="absolute bottom-full right-0 mb-2 w-56 break-words bg-gray-700 text-white text-sm px-2 py-1 rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200">
                                            {data.toolTip}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </React.Fragment>
            </div>
        </div>
    );
};

export default Plan;
